import styled, { css } from 'styled-components';
import { isSafari } from '../../helpers/detectBrowser';
import { loading } from '../../theme/animations';
import Typography from '../Typography';
import Icon from '../Icon';

export const BorderCutWrapper = styled.span`
    overflow: hidden;
    display: block;
    border-radius: 14px;
    margin-bottom: 4px;
`;

export const InputWrapper = styled.div<{
    isMultiline?: boolean;
    withStaticLabel?: boolean;
    isFocused?: boolean;
    isError?: boolean;
    disabled?: boolean;
    height?: string;
}>`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    position: relative;
    min-height: 52px;
    ${isSafari ? 'border-radius: 14px;' : ''}
    padding: 8px 12px;
    background: ${props => props.theme.root.input.root.backgroundColor};
    box-sizing: border-box;
    transition: padding-top 0.2s, border-color 0.2s;
    height: ${props => props.height};

    ${props =>
        props.disabled &&
        css`
            background: ${props.theme.root.colors.basicColors['4%']};
        `}
    ${props =>
        props.isError &&
        css`
            background: ${props.theme.root.colors.system['danger10%']};
        `}
    ${props =>
        props.isFocused &&
        css`
            background: ${props.theme.root.input.focused.background};
        `}
    ${props =>
        props.isFocused &&
        props.isError &&
        css`
            border-color: ${props.theme.root.input.error.borderColor};
        `}
    &[data-error='true'] {
        border-color: ${props => props.theme.root.input.focused.borderColor};
        ${props =>
            props.isError &&
            css`
                border-color: ${props.theme.root.input.error.borderColor};
            `}
    }

    ${props =>
        props.withStaticLabel &&
        css`
            padding-top: 20px;
        `}
    ${props =>
        props.isMultiline &&
        css`
            height: ${props.height || '84px'};
        `}
    
    input, textarea {
        width: auto;
        flex: 1;
    }
`;

interface TextFieldBaseProps {
    isError?: boolean;
    disabled?: boolean;
    withPrefix?: string;
    $showClear?: boolean;
}

const TextFieldBaseMixin = css<TextFieldBaseProps>`
    box-sizing: border-box;
    line-height: 18px;
    font-size: 14px;
    color: ${props => props.theme.root.input.root.color};
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    padding: 0 ${props => (props.$showClear ? '44px' : 0)} 0 0;

    // Change WebKit user agent autocomplete styles
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
        -webkit-text-fill-color: ${props => props.theme.root.input.root.color};
        caret-color: ${props => props.theme.root.input.root.color};
        transition: background-color 5000s ease-in-out 0s;
    }

    &::placeholder {
        color: ${props => (props.withPrefix ? 'transparent' : props.theme.root.input.placeholder.color)};
        ${props =>
            props.disabled &&
            css`
                color: ${props.theme.root.input.placeholderDisabled.color};
            `}
    }

    ${props =>
        props.disabled &&
        css`
            color: ${props.theme.root.colors.basicColors['25%']};
        `}
`;

export const Input = styled.input<TextFieldBaseProps>`
    ${TextFieldBaseMixin};
    background: transparent;
`;

export const Textarea = styled.textarea<TextFieldBaseProps>`
    ${TextFieldBaseMixin};
    padding-top: 10px;
    height: 100%;
`;

export const Label = styled.label<{ width?: string; removeOuterWith?: boolean }>`
    ${props =>
        !props.removeOuterWith &&
        css`
            min-width: 200px;
        `}
    width: ${props => props.width || 'auto'};
    display: block;
`;

export const Tip = styled(Typography)<{
    isMultiline?: boolean;
    isError: boolean;
    disabled?: boolean;
    isSuccess?: boolean;
    width?: string;
}>`
    padding: 0 12px;
    color: ${props => props.theme.root.input.label.color};
    ${props =>
        props.isMultiline &&
        css`
            justify-content: flex-end;
        `}

    ${props =>
        props.isError &&
        css`
            color: ${props.theme.root.input.error.borderColor};
        `}

    ${props =>
        props.isSuccess &&
        css`
            color: ${props.theme.root.input.successLabel.color};
        `}

    ${props =>
        props.disabled &&
        css`
            color: ${props.theme.root.input.placeholderDisabled.color};
        `}
`;

export const StaticLabel = styled(Typography)<{ isActive?: boolean; isError?: boolean; disabled?: boolean }>`
    color: ${props =>
        props.disabled ? props.theme.root.colors.basicColors['25%'] : props.theme.root.colors.basicColors['50%']};
    position: absolute;
    top: 6px;
    left: 12px;
    ${props =>
        props.isError &&
        css`
            color: ${props.theme.root.input.error.borderColor};
        `}
`;

// search input
export const SearchBorderCutWrapper = styled.div`
    overflow: hidden;
    border-radius: 100px;
    flex: 1;
`;

export const SearchInputWrapper = styled.div<{ width?: string; isFullWidth?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    padding-right: 8px;

    ${props =>
        props.width &&
        css`
            width: ${props.width};
        `}
    ${props =>
        props.isFullWidth &&
        css`
            flex: 1;
        `}
`;

export const SearchField = styled.input<{
    $isError?: boolean;
    disabled?: boolean;
    $isTransparent?: boolean;
    $isSmallSize?: boolean;
}>`
    box-sizing: border-box;
    width: 100%;
    line-height: 24px;
    font-size: 15px;
    padding: ${props => (props.$isSmallSize ? '8px 2px 8px 8px' : '12px 2px 12px 56px')};
    letter-spacing: 0.1px;
    background: ${props => (props.$isTransparent ? 'transparent' : props.theme.root.input.root.backgroundColor)};
    color: ${props => props.theme.root.input.root.color};
    border: none;
    border-bottom: 2px solid ${props => props.theme.root.input.root.borderColor};
    transition: border-color 0.3s ease;
    outline: none;

    &:focus,
    &[data-error='true'] {
        border-color: ${props => (props.$isTransparent ? 'transparent' : props.theme.root.input.focused.borderColor)};
        ${props =>
            props.$isError &&
            css`
                border-color: ${props.theme.root.input.error.borderColor};
            `}
    }

    &::placeholder {
        color: ${props => props.theme.root.input.placeholder.color};
        ${props =>
            props.disabled &&
            css`
                color: ${props.theme.root.input.placeholderDisabled.color};
            `}
    }
    ${props =>
        props.disabled &&
        css`
            background: ${props.theme.root.input.disabled.background};
        `}
`;

export const LoadingIcon = styled(Icon)`
    ${loading};
`;

export const ClearButton = styled.div<{ withFocus?: boolean }>`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    ${({ theme, withFocus }) =>
        withFocus &&
        `
            &:focus {
                border-radius: 50%;
                background-color: ${theme.root.colors.basicColors['6%']};
            }
        `}
`;

export const FilterButton = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 32px;
    background-color: ${props => props.theme.root.colors.basicColors['4%']};
    cursor: pointer;
`;

export const PhoneInputWrapper = styled.div`
    display: flex;
    justify-content: stretch;
    width: 100%;
`;

export const PhoneErrorWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    flex-grow: 1;
    margin-left: 8px;

    ${InputWrapper} {
        border-radius: 14px;
        margin-bottom: 4px;
    }
`;

export const TipWrapper = styled.div`
    display: flex;
    justify-content: space-between;

    ${LoadingIcon} {
        position: absolute;
        right: 8px;
        top: calc(50% - 8px);
        transform: translateY(-50%);
    }
`;

export const Counter = styled(Typography)`
    color: ${props => props.theme.root.input.label.color};
    line-height: 14px;
    justify-self: flex-end;
    margin-left: auto;
    padding: 0 12px;
`;
